import {
  hospitalService,
  type HospitalDetailResponse,
  type HospitalInfoResponse,
  type HospitalRegionOption,
  type HospitalSearchRequest,
  type HospitalUpdateRequest,
} from '@/entities/hospital/api/hospital.api';
import { businessKeys } from '@/entities/business/hooks/useBusinessRelationships';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { uploadFileToPresignedUrl } from '@/shared/utils/fileUpload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** 병의원 목록 캐시 키 — 상세·수정 후 목록 invalidate 시 사용 */
export const hospitalKeys = {
  all: ['hospitals'] as const,
  lists: () => [...hospitalKeys.all, 'list'] as const,
  list: (search: HospitalSearchRequest, pageable: Pageable) =>
    [...hospitalKeys.lists(), search, pageable] as const,
  regions: () => [...hospitalKeys.all, 'regions'] as const,
  details: () => [...hospitalKeys.all, 'detail'] as const,
  detail: (clinicId: number) => [...hospitalKeys.details(), clinicId] as const,
};

export interface HospitalListParams {
  search: HospitalSearchRequest;
  pageable: Pageable;
  /**
   * URL에 지역이 있으면 선택지 로딩(또는 실패) 뒤에 목록을 친다.
   * 선택지에 없는 값을 보내 400이 나는 걸 막는다.
   */
  enabled?: boolean;
}

/**
 * 병의원 시/도·시/군/구 선택지.
 *
 * 저장된 주소에서 뽑은 값이라 병의원이 없는 지역은 없다.
 * 거의 바뀌지 않는 선택지라 전역 기본(staleTime 0)과 달리 5분 동안 다시 받지 않는다.
 */
export const useHospitalRegions = () =>
  useQuery<HospitalRegionOption[]>({
    queryKey: hospitalKeys.regions(),
    queryFn: hospitalService.getHospitalRegions,
    staleTime: 5 * 60 * 1000,
  });

/**
 * 병의원 목록 조회.
 *
 * 레거시 `hospitalInfo.js`와 동일하게 서버 페이지네이션을 사용한다.
 * 검색어·진료과·영업상태(`OPEN`/`SUSPENDED`/`CLOSED`, 전체는 생략)와
 * 시/도·시/군/구를 `HospitalSearchRequest`로 전달한다.
 */
export const useHospitalList = ({
  search,
  pageable,
  enabled,
}: HospitalListParams) =>
  usePaginatedQuery<HospitalSearchRequest, HospitalInfoResponse>({
    queryKeyPrefix: hospitalKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      hospitalService.getHospitals(s, p),
    enabled,
  });

/**
 * 병의원 목록 엑셀.
 * 서버가 파일을 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportHospitals = () =>
  useMutation({
    mutationFn: (search: HospitalSearchRequest) =>
      hospitalService.exportHospitals(search),
  });

/**
 * 병의원 상세 조회.
 * 레거시 `hospitalInfoDetails.js` 상세 패널에 대응한다.
 */
export const useHospitalDetail = (clinicId: number | null) =>
  useQuery({
    queryKey: clinicId != null ? hospitalKeys.detail(clinicId) : [],
    queryFn: async () => {
      if (clinicId == null) throw new Error('clinicId is required');
      return hospitalService.getHospitalDetail(clinicId);
    },
    enabled: clinicId != null,
  });

/**
 * 병의원 정보 수정.
 * 저장 성공 시 목록은 invalidate, 상세는 요청 페이로드로 캐시를 merge한다.
 * (상세 GET이 진료과 등 일부 필드를 늦게/누락 반영해도 UI가 저장값으로 유지되도록)
 */
export const useUpdateHospital = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clinicId,
      data,
    }: {
      clinicId: number;
      data: HospitalUpdateRequest;
    }) => hospitalService.updateHospital(clinicId, data),
    onSuccess: (_data, { clinicId, data: updateData }) => {
      queryClient.setQueryData(
        hospitalKeys.detail(clinicId),
        (old: HospitalDetailResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            ...updateData,
            ...(updateData.medicalDepartments != null
              ? { medicalDepartments: updateData.medicalDepartments }
              : {}),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: hospitalKeys.lists() });
      // 주소 수정 시 시/도 선택지가 바뀔 수 있다
      queryClient.invalidateQueries({ queryKey: hospitalKeys.regions() });
    },
  });
};

/**
 * 병의원 사업자등록증 업로드.
 * presigned URL 발급 → S3 PUT → complete 로 clinic 마스터 이미지를 갱신한다.
 */
export const useUploadHospitalBusinessLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clinicId,
      file,
    }: {
      clinicId: number;
      file: File;
      relationPharmaCompanyId?: number;
      drugCode?: string | null;
    }) => {
      const { presignedUrl, uploadKey } =
        await hospitalService.generateBusinessLicensePresignedUrl({
          filename: file.name,
        });

      if (!uploadKey) {
        throw new Error('uploadKey가 없습니다.');
      }

      await uploadFileToPresignedUrl(file, presignedUrl);
      await hospitalService.completeBusinessLicenseUpload({
        clinicId,
        uploadKey,
      });
    },
    onSuccess: (_data, { clinicId, relationPharmaCompanyId, drugCode }) => {
      queryClient.invalidateQueries({
        queryKey: hospitalKeys.detail(clinicId),
      });
      if (relationPharmaCompanyId != null) {
        queryClient.invalidateQueries({
          queryKey: [...businessKeys.details(), relationPharmaCompanyId],
        });
        queryClient.invalidateQueries({
          queryKey: businessKeys.detail(relationPharmaCompanyId, drugCode),
        });
      }
    },
  });
};
